"use client"

import { Badge } from "@/components/ui/badge"
import { UserRound } from "lucide-react"
import { useAgentRolesQuery } from "@/hooks/queries/ai-use-case-roles"
import { DEFAULT_ROLE_SLUG, isCustomOnly } from "@/lib/ai/roles/types"

/** Roles shown before the overflow counter — three chips crowd a card row. */
const VISIBLE_ROLES = 2

interface AgentRoleBadgeProps {
  useCase: string
  roleSlugs: string[] | undefined
  /** Tenant-invented custom-role name (config.role_label). */
  roleLabel?: string | null
}

/** Subtle role indicator for agent cards: one chip per role, overflow as "+N". */
export function AgentRoleBadge({ useCase, roleSlugs, roleLabel }: AgentRoleBadgeProps) {
  const { data: roles = [] } = useAgentRolesQuery(useCase)

  const slugs = roleSlugs?.length ? roleSlugs : [DEFAULT_ROLE_SLUG]
  const label = isCustomOnly(slugs) ? roleLabel?.trim() : undefined
  const names = slugs.map(
    (slug) => label || roles.find((r) => r.slug === slug)?.name || slug,
  )

  const visible = names.slice(0, VISIBLE_ROLES)
  const hidden = names.length - visible.length

  return (
    <div className="flex flex-wrap items-center gap-1">
      {visible.map((name, index) => (
        <Badge key={slugs[index]} variant="outline" className="gap-1 font-normal text-muted-foreground">
          {index === 0 && <UserRound className="h-3 w-3" />}
          {name}
        </Badge>
      ))}
      {hidden > 0 && (
        <Badge
          variant="outline"
          className="font-normal text-muted-foreground"
          title={names.slice(VISIBLE_ROLES).join(", ")}
        >
          <span>+</span>{hidden}
        </Badge>
      )}
    </div>
  )
}
